import { isCameraShareEnabled, isScreenShareEnabled } from "./media-sharing-config-utils";

function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeRoomResourceSetting(value) {
  const parsed = typeof value === "string" && value.trim() !== "" ? Number(value) : value;

  if (typeof parsed !== "number" || !Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return Math.floor(parsed);
}

const SUBSCRIPTION_LIMIT_KEYS = Object.freeze({
  room_size: "subscriptionMaxCCU",
  maxCCU: "subscriptionMaxCCU",
  maxMicrophones: "subscriptionMaxMicrophones",
  maxCameras: "subscriptionMaxCameras",
  maxScreenShares: "subscriptionMaxScreenShares"
});

function roomResourceSubscriptionLimit(sessionLimits, limitKey) {
  const subscriptionLimit = normalizeRoomResourceSetting(sessionLimits?.[SUBSCRIPTION_LIMIT_KEYS[limitKey]]);

  if (subscriptionLimit !== null) {
    return subscriptionLimit;
  }

  return normalizeRoomResourceSetting(sessionLimits?.[limitKey]);
}

export function roomResourceSettingValue(settings, sessionLimits, limitKey) {
  const override = normalizeRoomResourceSetting(settings?.user_data?.resourceLimits?.[limitKey]);

  if (override !== null) {
    return override;
  }

  const sessionLimit = normalizeRoomResourceSetting(sessionLimits?.[limitKey]);

  return sessionLimit === null ? "" : sessionLimit;
}

export function roomResourceSettingMax(sessionLimits, limitKey) {
  const sessionLimit = roomResourceSubscriptionLimit(sessionLimits, limitKey);

  return sessionLimit === null ? undefined : sessionLimit;
}

export function roomParticipantLimitMax(configMaxRoomSize, sessionLimits) {
  const configMax = normalizeRoomResourceSetting(configMaxRoomSize);
  const sessionMax =
    roomResourceSubscriptionLimit(sessionLimits, "maxCCU") ?? normalizeRoomResourceSetting(sessionLimits?.maxCCU);

  if (configMax === null) {
    return sessionMax === null ? undefined : sessionMax;
  }

  if (sessionMax === null) {
    return configMax;
  }

  return Math.min(configMax, sessionMax);
}

export function nextRoomParticipantLimitSettings(settings, value, configMaxRoomSize, sessionLimits) {
  const nextSettings = {
    ...(settings || {})
  };
  const nextLimit = value === "" ? "" : normalizeRoomResourceSetting(value);

  if (nextLimit === null) {
    nextSettings.room_size = value;
    return nextSettings;
  }

  const max = roomParticipantLimitMax(configMaxRoomSize, sessionLimits);
  nextSettings.room_size = max === undefined ? nextLimit : Math.min(nextLimit, max);

  return nextSettings;
}

export function nextRoomResourceLimitSettings(settings, limitKey, value, sessionLimits) {
  const nextSettings = {
    ...(settings || {}),
    user_data: {
      ...((isRecord(settings?.user_data) && settings.user_data) || {})
    }
  };
  const resourceLimits = {
    ...((isRecord(settings?.user_data?.resourceLimits) && settings.user_data.resourceLimits) || {})
  };
  const nextLimit = normalizeRoomResourceSetting(value);

  if (nextLimit === null) {
    delete resourceLimits[limitKey];
  } else {
    const max = roomResourceSettingMax(sessionLimits, limitKey);
    resourceLimits[limitKey] = max === undefined ? nextLimit : Math.min(nextLimit, max);
  }

  nextSettings.user_data.resourceLimits = resourceLimits;

  return nextSettings;
}

export function roomResourceSettingsAvailability(config = {}, settings = {}) {
  return {
    maxMicrophones: {
      visible: config?.voiceChat?.enabled !== false,
      disabled: false
    },
    maxCameras: {
      visible: isCameraShareEnabled(config),
      disabled: false
    },
    maxScreenShares: {
      visible: isScreenShareEnabled(config),
      disabled: false
    }
  };
}
